import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  getLegalEntities, createLegalEntity, deleteLegalEntity, 
  getOperatingEntities, createOperatingEntity, deleteOperatingEntity, 
  seedData, getCalendarStatus, getCalendarAuthUrl, disconnectCalendar,
  listUsers, inviteUser, updateUserRole
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Building, Building2, Trash2, Play, Calendar, Users, CheckCircle, XCircle, ExternalLink, UserPlus, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';

const SettingsPage = () => {
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const isFounder = user?.role === 'Founder';
  
  const [legalEntities, setLegalEntities] = useState([]);
  const [operatingEntities, setOperatingEntities] = useState([]);
  const [users, setUsers] = useState([]);
  const [calendarStatus, setCalendarStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [legalDialogOpen, setLegalDialogOpen] = useState(false);
  const [operatingDialogOpen, setOperatingDialogOpen] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [legalForm, setLegalForm] = useState({ name: '', jurisdiction: '', entity_type: '' });
  const [operatingForm, setOperatingForm] = useState({ name: '', description: '' });
  const [inviteForm, setInviteForm] = useState({ name: '', email: '', password: '', role: 'Core Team' });

  const fetchData = async () => {
    try {
      const [legalRes, operatingRes, calendarRes] = await Promise.all([
        getLegalEntities(),
        getOperatingEntities(),
        getCalendarStatus().catch(() => ({ data: { connected: false } }))
      ]);
      setLegalEntities(legalRes.data);
      setOperatingEntities(operatingRes.data);
      setCalendarStatus(calendarRes.data);
      
      // Fetch users if Founder
      if (isFounder) {
        try {
          const usersRes = await listUsers();
          setUsers(usersRes.data.users || []);
        } catch (e) {
          console.error('Failed to load users:', e);
        }
      }
    } catch (error) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    
    // Check for calendar connection callback
    const calendarParam = searchParams.get('calendar');
    if (calendarParam === 'connected') {
      toast.success('Google Calendar connected successfully!');
    } else if (calendarParam === 'error') {
      toast.error('Failed to connect Google Calendar');
    }
  }, [searchParams]);

  const handleSeedData = async () => {
    try {
      await seedData();
      toast.success('Initial data seeded');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to seed data');
    }
  };

  const handleConnectCalendar = async () => {
    try {
      const response = await getCalendarAuthUrl();
      window.location.href = response.data.auth_url;
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Google OAuth not configured. See setup instructions below.');
      } else {
        toast.error('Failed to get authorization URL');
      }
    }
  };

  const handleDisconnectCalendar = async () => {
    if (!window.confirm('Disconnect Google Calendar?')) return;
    try {
      await disconnectCalendar();
      toast.success('Calendar disconnected');
      fetchData();
    } catch (error) {
      toast.error('Failed to disconnect');
    }
  };

  const handleCreateLegal = async (e) => {
    e.preventDefault();
    try {
      await createLegalEntity(legalForm);
      toast.success('Legal entity created');
      setLegalDialogOpen(false);
      setLegalForm({ name: '', jurisdiction: '', entity_type: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create');
    }
  };

  const handleCreateOperating = async (e) => {
    e.preventDefault();
    try {
      await createOperatingEntity(operatingForm);
      toast.success('Operating entity created');
      setOperatingDialogOpen(false);
      setOperatingForm({ name: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to create');
    }
  };

  const handleInviteUser = async (e) => {
    e.preventDefault();
    try {
      await inviteUser(inviteForm);
      toast.success(`User ${inviteForm.email} invited as ${inviteForm.role}`);
      setInviteDialogOpen(false);
      setInviteForm({ name: '', email: '', password: '', role: 'Core Team' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to invite user');
    }
  };

  const handleUpdateRole = async (userId, newRole) => {
    try {
      await updateUserRole(userId, newRole);
      toast.success('Role updated');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const handleDeleteLegal = async (id) => {
    if (!window.confirm('Delete this legal entity?')) return;
    try {
      await deleteLegalEntity(id);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleDeleteOperating = async (id) => {
    if (!window.confirm('Delete this operating entity?')) return;
    try {
      await deleteOperatingEntity(id);
      toast.success('Deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64 text-zinc-500 font-inter">Loading...</div>;
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-manrope text-xl font-bold text-zinc-900">Settings</h1>
          <p className="text-sm text-zinc-500 font-inter">
            Manage entities, integrations, and users
            {user && <Badge variant="outline" className="ml-2">{user.role}</Badge>}
          </p>
        </div>
        {legalEntities.length === 0 && operatingEntities.length === 0 && (
          <Button onClick={handleSeedData} className="rounded-sm" data-testid="seed-data-btn">
            <Play className="w-4 h-4 mr-1" /> Seed Initial Data
          </Button>
        )}
      </div>

      <Tabs defaultValue="integrations" className="w-full">
        <TabsList className="border-b border-zinc-200 w-full justify-start rounded-none bg-transparent p-0">
          <TabsTrigger value="integrations" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <Calendar className="w-4 h-4 mr-1" /> Integrations
          </TabsTrigger>
          {isFounder && (
            <TabsTrigger value="users" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
              <Users className="w-4 h-4 mr-1" /> Team ({users.length})
            </TabsTrigger>
          )}
          <TabsTrigger value="legal" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <Building className="w-4 h-4 mr-1" /> Legal Entities ({legalEntities.length})
          </TabsTrigger>
          <TabsTrigger value="operating" className="rounded-none border-b-2 border-transparent data-[state=active]:border-zinc-900">
            <Building2 className="w-4 h-4 mr-1" /> Operating Entities ({operatingEntities.length})
          </TabsTrigger>
        </TabsList>

        {/* Integrations Tab */}
        <TabsContent value="integrations" className="pt-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Google Calendar Integration */}
            <Card className="border border-zinc-200 shadow-none rounded-sm">
              <CardHeader className="pb-2">
                <CardTitle className="font-manrope text-base flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  Google Calendar
                  {calendarStatus?.connected ? (
                    <Badge className="bg-green-100 text-green-800">Connected</Badge>
                  ) : (
                    <Badge variant="outline">Not Connected</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-500 font-inter mb-4">
                  Connect your Google Calendar to see upcoming meetings on the dashboard and capture them as evidence.
                </p>
                {calendarStatus?.connected ? (
                  <div className="space-y-3">
                    {/* Enhanced Status Section */}
                    <div className="bg-green-50 border border-green-200 rounded-sm p-3 space-y-2">
                      <div className="flex items-center gap-2 text-green-700 text-sm font-medium">
                        <CheckCircle className="w-4 h-4" />
                        Calendar Connected
                      </div>
                      {calendarStatus.connected_email && (
                        <p className="text-xs text-green-600 font-mono">
                          Account: {calendarStatus.connected_email}
                        </p>
                      )}
                      {calendarStatus.connected_at && (
                        <p className="text-xs text-green-600">
                          Connected: {new Date(calendarStatus.connected_at).toLocaleDateString()}
                        </p>
                      )}
                      {calendarStatus.last_sync && (
                        <p className="text-xs text-green-600">
                          Last Sync: {new Date(calendarStatus.last_sync).toLocaleString()}
                        </p>
                      )}
                      {calendarStatus.scopes_granted?.length > 0 && (
                        <div className="text-xs text-green-600">
                          <p className="font-medium">Scopes:</p>
                          <ul className="list-disc list-inside ml-2">
                            {calendarStatus.scopes_granted.map((scope, i) => (
                              <li key={i} className="truncate">{scope.split('/').pop()}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                    {isFounder && (
                      <Button variant="outline" size="sm" onClick={handleDisconnectCalendar} className="rounded-sm text-red-600">
                        <XCircle className="w-4 h-4 mr-1" /> Disconnect
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {calendarStatus?.setup_required || !calendarStatus?.app_base_url_configured ? (
                      <div className="bg-amber-50 border border-amber-200 rounded-sm p-3">
                        <p className="text-sm font-medium text-amber-800 mb-2">Setup Required</p>
                        <p className="text-xs text-amber-700 mb-2">
                          {!calendarStatus?.app_base_url_configured 
                            ? "APP_BASE_URL not configured for OAuth." 
                            : "Google OAuth credentials not configured."}
                        </p>
                        <ol className="text-xs text-amber-700 list-decimal list-inside space-y-1">
                          <li>Go to Google Cloud Console</li>
                          <li>Create OAuth 2.0 credentials</li>
                          <li>Enable Calendar and Gmail APIs</li>
                          <li>Add credentials to backend/.env</li>
                        </ol>
                        <a 
                          href="https://console.cloud.google.com/" 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-amber-800 hover:underline mt-2"
                        >
                          Open Google Cloud Console <ExternalLink className="w-3 h-3" />
                        </a>
                      </div>
                    ) : isFounder ? (
                      <Button onClick={handleConnectCalendar} className="rounded-sm">
                        <Calendar className="w-4 h-4 mr-1" /> Connect Google Calendar
                      </Button>
                    ) : (
                      <p className="text-sm text-zinc-500">Only Founder can connect calendar</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Gmail Integration */}
            <Card className="border border-zinc-200 shadow-none rounded-sm">
              <CardHeader className="pb-2">
                <CardTitle className="font-manrope text-base flex items-center gap-2">
                  <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M20 18h-2V9.25L12 13 6 9.25V18H4V6h1.2l6.8 4.25L18.8 6H20v12z"/>
                  </svg>
                  Gmail (Outbound)
                  {calendarStatus?.connected ? (
                    <Badge className="bg-green-100 text-green-800">Connected</Badge>
                  ) : (
                    <Badge variant="outline">Not Connected</Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-500 font-inter mb-4">
                  Send approved draft messages via Gmail. Requires Google OAuth (same connection as Calendar).
                </p>
                {calendarStatus?.connected ? (
                  <div className="flex items-center gap-2 text-green-600 text-sm">
                    <CheckCircle className="w-4 h-4" />
                    Gmail sending enabled
                  </div>
                ) : (
                  <p className="text-sm text-zinc-400">Connect Google Calendar above to enable Gmail</p>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Users Tab (Founder only) */}
        {isFounder && (
          <TabsContent value="users" className="pt-4">
            <div className="flex justify-end mb-4">
              <Dialog open={inviteDialogOpen} onOpenChange={setInviteDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="rounded-sm">
                    <UserPlus className="w-4 h-4 mr-1" /> Invite User
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-sm">
                  <DialogHeader>
                    <DialogTitle className="font-manrope">Invite New User</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleInviteUser} className="space-y-4">
                    <div>
                      <Label className="font-inter text-sm">Name</Label>
                      <Input
                        value={inviteForm.name}
                        onChange={(e) => setInviteForm({ ...inviteForm, name: e.target.value })}
                        className="rounded-sm"
                        placeholder="Full name"
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-inter text-sm">Email</Label>
                      <Input
                        type="email"
                        value={inviteForm.email}
                        onChange={(e) => setInviteForm({ ...inviteForm, email: e.target.value })}
                        className="rounded-sm"
                        placeholder="email@example.com"
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-inter text-sm">Temporary Password</Label>
                      <Input
                        type="password"
                        value={inviteForm.password}
                        onChange={(e) => setInviteForm({ ...inviteForm, password: e.target.value })}
                        className="rounded-sm"
                        placeholder="Initial password"
                        required
                      />
                    </div>
                    <div>
                      <Label className="font-inter text-sm">Role</Label>
                      <Select value={inviteForm.role} onValueChange={(value) => setInviteForm({ ...inviteForm, role: value })}>
                        <SelectTrigger className="rounded-sm">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Core Team">Core Team (View Only Approvals)</SelectItem>
                          <SelectItem value="Founder">Founder (Full Access)</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <Button type="submit" className="w-full rounded-sm">Send Invite</Button>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            <Card className="border border-zinc-200 shadow-none rounded-sm">
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-zinc-50">
                    <tr>
                      <th className="text-left p-3 text-xs font-medium text-zinc-500 uppercase">Name</th>
                      <th className="text-left p-3 text-xs font-medium text-zinc-500 uppercase">Email</th>
                      <th className="text-left p-3 text-xs font-medium text-zinc-500 uppercase">Role</th>
                      <th className="text-left p-3 text-xs font-medium text-zinc-500 uppercase">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id} className="border-t border-zinc-100">
                        <td className="p-3 font-inter text-sm">{u.name}</td>
                        <td className="p-3 font-mono text-sm text-zinc-600">{u.email}</td>
                        <td className="p-3">
                          <Badge className={u.role === 'Founder' ? 'bg-purple-100 text-purple-800' : 'bg-zinc-100 text-zinc-800'}>
                            <Shield className="w-3 h-3 mr-1" />
                            {u.role}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {u.id !== user?.id && (
                            <Select 
                              value={u.role} 
                              onValueChange={(value) => handleUpdateRole(u.id, value)}
                            >
                              <SelectTrigger className="w-32 h-8 rounded-sm text-xs">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="Core Team">Core Team</SelectItem>
                                <SelectItem value="Founder">Founder</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {u.id === user?.id && <span className="text-xs text-zinc-400">You</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>

            <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-sm">
              <h4 className="font-medium text-blue-800 text-sm mb-2">Role Permissions</h4>
              <div className="grid grid-cols-2 gap-4 text-xs text-blue-700">
                <div>
                  <p className="font-medium">Founder</p>
                  <ul className="list-disc list-inside">
                    <li>Full system access</li>
                    <li>Approve/reject drafts</li>
                    <li>Send outbound messages</li>
                    <li>Manage team members</li>
                    <li>Connect integrations</li>
                  </ul>
                </div>
                <div>
                  <p className="font-medium">Core Team</p>
                  <ul className="list-disc list-inside">
                    <li>View all data</li>
                    <li>Create threads/evidence</li>
                    <li>Create drafts</li>
                    <li>Cannot approve/send</li>
                    <li>Cannot manage team</li>
                  </ul>
                </div>
              </div>
            </div>
          </TabsContent>
        )}

        {/* Legal Entities */}
        <TabsContent value="legal" className="pt-4">
          <div className="flex justify-end mb-4">
            <Dialog open={legalDialogOpen} onOpenChange={setLegalDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="add-legal-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Legal Entity
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Add Legal Entity</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateLegal} className="space-y-4">
                  <div>
                    <Label className="font-inter text-sm">Name</Label>
                    <Input
                      value={legalForm.name}
                      onChange={(e) => setLegalForm({ ...legalForm, name: e.target.value })}
                      className="rounded-sm"
                      placeholder="Dharmadhara Trust"
                      required
                    />
                  </div>
                  <div>
                    <Label className="font-inter text-sm">Jurisdiction</Label>
                    <Input
                      value={legalForm.jurisdiction}
                      onChange={(e) => setLegalForm({ ...legalForm, jurisdiction: e.target.value })}
                      className="rounded-sm"
                      placeholder="India, USA, Singapore..."
                      required
                    />
                  </div>
                  <div>
                    <Label className="font-inter text-sm">Entity Type</Label>
                    <Input
                      value={legalForm.entity_type}
                      onChange={(e) => setLegalForm({ ...legalForm, entity_type: e.target.value })}
                      className="rounded-sm"
                      placeholder="Trust, Pvt Ltd, LLC, Corp..."
                      required
                    />
                  </div>
                  <Button type="submit" className="w-full rounded-sm">Create</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>
          
          {legalEntities.length === 0 ? (
            <Card className="border border-zinc-200 shadow-none rounded-sm">
              <CardContent className="p-8 text-center">
                <Building className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-zinc-500 font-inter">No legal entities. Add one or seed initial data.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {legalEntities.map(entity => (
                <Card key={entity.id} className="border border-zinc-200 shadow-none rounded-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium font-inter text-zinc-900">{entity.name}</h3>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="outline" className="text-xs">{entity.entity_type}</Badge>
                          <span className="text-xs text-zinc-500 font-mono">{entity.jurisdiction}</span>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-zinc-400 hover:text-red-500"
                        onClick={() => handleDeleteLegal(entity.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Operating Entities */}
        <TabsContent value="operating" className="pt-4">
          <div className="flex justify-end mb-4">
            <Dialog open={operatingDialogOpen} onOpenChange={setOperatingDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-sm" data-testid="add-operating-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Operating Entity
                </Button>
              </DialogTrigger>
              <DialogContent className="rounded-sm">
                <DialogHeader>
                  <DialogTitle className="font-manrope">Add Operating Entity</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleCreateOperating} className="space-y-4">
                  <div>
                    <Label className="font-inter text-sm">Name</Label>
                    <Input
                      value={operatingForm.name}
                      onChange={(e) => setOperatingForm({ ...operatingForm, name: e.target.value })}
                      className="rounded-sm"
                      placeholder="VCAP, Dharmadhara..."
                      required
                    />
                  </div>
                  <div>
                    <Label className="font-inter text-sm">Description</Label>
                    <Input
                      value={operatingForm.description}
                      onChange={(e) => setOperatingForm({ ...operatingForm, description: e.target.value })}
                      className="rounded-sm"
                      placeholder="Business line description..."
                    />
                  </div>
                  <Button type="submit" className="w-full rounded-sm">Create</Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {operatingEntities.length === 0 ? (
            <Card className="border border-zinc-200 shadow-none rounded-sm">
              <CardContent className="p-8 text-center">
                <Building2 className="w-8 h-8 text-zinc-300 mx-auto mb-2" />
                <p className="text-zinc-500 font-inter">No operating entities. Add one or seed initial data.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {operatingEntities.map(entity => (
                <Card key={entity.id} className="border border-zinc-200 shadow-none rounded-sm">
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="font-medium font-inter text-zinc-900">{entity.name}</h3>
                        <p className="text-sm text-zinc-500 font-inter mt-1">{entity.description || 'No description'}</p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="text-zinc-400 hover:text-red-500"
                        onClick={() => handleDeleteOperating(entity.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SettingsPage;
